import React, { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDialogShadow } from "@evergis/ui";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { ImportErrors } from "./ImportErrors";
import { ImportSuccess } from "./ImportSuccess";
import { ImportProgressBlock } from "./ProgressBlock";
import { GDBImportWizard } from "./GDBImportWizard";

import { useContext } from "../hooks/useContext";
import { useImportContext } from "../importContext";
import { useGDBImportJob } from "../hooks/useGDBImportJob";

import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";

const GDBImportFinishComponent: FC = () => {
  const { t } = useTranslation("common");
  const context = useContext();
  const { shadows, setScrollContainer } = useDialogShadow();
  const { prevStep, dataSchema, objects } = useImportContext();
  const { copyingLayers, layersCreating, isImportDone, processed, error, onApply, onCancel } = useGDBImportJob();

  const getObjectsCount = useMemo(() => {
    return objects
      ? objects.reduce((result, object) => (object.chip > 0 ? result + Number(object.chip) : result), 0)
      : (dataSchema as SchemaParsed).objectCount;
  }, [objects, dataSchema]);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} extra={<GDBImportWizard />} />
      <DialogContent ref={setScrollContainer} style={{ paddingTop: "0" }}>
        <RestrictedWidthContainer>
          {(copyingLayers || layersCreating) && !isImportDone && (
            <ImportProgressBlock
              importInProgress={copyingLayers}
              layerCreating={layersCreating}
              estimatedRecords={getObjectsCount}
              processedRecords={processed}
            />
          )}
          {isImportDone && !error.message && <ImportSuccess objectCount={processed} totalCount={getObjectsCount} />}
          {!!error.message && <ImportErrors error={error} importInProgress={copyingLayers} />}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        isWaiting={!isImportDone}
        applyText={t("actions.ready")}
        backButtonText={t("import.back", { ns: "dialogs" })}
        onBackButton={prevStep}
        onCancel={onCancel}
        onApply={onApply}
      />
    </FullScreenDialog>
  );
};

export const GDBImportFinish = memo(GDBImportFinishComponent);
